import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getMembershipFormConfig, getListingConfig, selectEventNameFromListingConfig } from '@b/checkout/selectors/ui';
import { getXssFilter } from '@showclix-shared-util/xssFilter';
import Button from '@s/Button';
import MessageInlineBanner from "@showclix-shared/MessageInlineBanner";
import { useTranslation } from '@c/Translations';
import { fetchReservation } from '@/actions/reservation';
import { wrappedFetch } from '@h/fetch';
import { setSkipMembership,setHasMembershipContext, membershipViewPage, membershipSuccessSignIn } from '@b/checkout/actions/ui';
import {getEventPriceLevels} from 'checkout/actions/event';
import { getEventSeriesChildListing } from '../../checkout/views/series/actions/event-series';
import { hasHookSubscription } from '@b/checkout/selectors/hooks';

const xssFilter = getXssFilter();

export const MemberLogin = () => {
  const dispatch = useDispatch();
  const eventName = useSelector(selectEventNameFromListingConfig);
  const formConfig = useSelector(getMembershipFormConfig);
  const listingConfig = useSelector(getListingConfig);
  const { name: sellerName } = useSelector((state) => state.contactInfo);
  const [ formValues, setFormValues ] = useState({});
  const [ membershipError, setMembershipError ] = useState(null);
  const { t } = useTranslation();
  const token = useSelector(state => state.reservation?.token);
  const secret = useSelector(state => state.reservation?.secret);
  const isRecurring = useSelector(state => state.ui.isRecurring);
  const selectedChildEvent = useSelector(state => state.event?.childEvent?.event);
  const hasMembershipViewPageSubscription = useSelector(state => hasHookSubscription(state, 'ADD_MEMBERSHIP_VIEW_HOOK'));
  const hasReportedMembershipView = useRef(false);

  const {
    membership_required,
    title_text,
    subtitle_text,
    additional_text,
    auth_fields = {},
    member_button,
    nonmember_button,
  } = formConfig || {};

  const title = title_text || (membership_required ? t('checkout__for_members_only') : t('checkout__membership_login'));
  const subtitle = subtitle_text || t('checkout__membership_subtitle', { sellerName});

  const additionalText = xssFilter.process(additional_text || '');

  useEffect(() => {
    if (!hasReportedMembershipView.current && hasMembershipViewPageSubscription) {
      dispatch(membershipViewPage());
      hasReportedMembershipView.current = true;
    }
  }, [hasMembershipViewPageSubscription]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    // Add only the fields from auth_fields
    if (formConfig.auth_fields[name]) {
      setMembershipError(null);
      setFormValues((prev) => ({...prev, [name]: value}));
    }
  }

  const memberLoginFormSubmit = (e) => {
    e.preventDefault();
    // Prepare payload
    const membershipData = {
      listingId: listingConfig.listingId,
      membership: {
        submit_member: 'submit_member',
        ...formValues
      },
    };

    const url = '/api/memberships/' + (token ?? '');
    const headers = {
        'Content-Type': 'application/json'
    };
    if (secret){
      headers['reservation-secret'] = secret;
    }
    // Send request
    wrappedFetch(url , {
      method: 'POST',
      mode: 'cors',
      headers: headers,
      body: JSON.stringify(membershipData),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.token && data.secret) {
          dispatch(setHasMembershipContext(true));
          dispatch(membershipSuccessSignIn());
          dispatch(fetchReservation(data.token, data.secret)).then(() => {
            if (isRecurring) {
              if (selectedChildEvent){
                dispatch(getEventSeriesChildListing(selectedChildEvent.event_id));
              }
            }else {
              dispatch(getEventPriceLevels());
            }
          });

        } else {
          setMembershipError(data.errors?.[0]?.detail ?? 'error');
        }
      });
  }

  const onSkipMembership = () => {
    dispatch(setSkipMembership(true));
  }

  return (
    <div className="listing__member-login">
      <div className="listing__member-login__content">
        <h1 className="listing__member-login__event-name">{eventName}</h1>
        <div className="listing__member-login__form">
          <header className="member-login__form__header">
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </header>
          <main className="member-login__form__main">
            <form
              className="member-login__form__auth"
              onSubmit={memberLoginFormSubmit}
              onChange={handleFormChange}
            >
              {Object.values(auth_fields).map(({id, label}) => (
                <input
                  type="text"
                  key={id}
                  id={id}
                  name={id}
                  placeholder={label}
                  autoComplete="off"
                  className="input__control"
                  required
                />
              ))}
              { membershipError && <MessageInlineBanner variant={MessageInlineBanner.variant.error}>
                <span dangerouslySetInnerHTML={{ __html: membershipError }}></span>
              </MessageInlineBanner> }
              <Button type="submit" className="button--primary" onClick={() => {}}>
                {member_button || (<>{t('checkout__continue')} &rsaquo;&rsaquo;</>)}
              </Button>
              {!membership_required && (
                <Button
                  type="button"
                  className="button--secondary"
                  onClick={onSkipMembership}
                >
                  {nonmember_button || (<>{t('checkout__not_a_member')} &rsaquo;&rsaquo;</>)}
                </Button>
              )}
            </form>
            <div
              className="member-login__form__additional-text"
              dangerouslySetInnerHTML={{ __html: additionalText }}
            />
          </main>
        </div>
      </div>
    </div>
  );
};
